import React, { useState, useEffect } from "react";
import {
  MenuItem,
  FormControl,
  FormHelperText,
  Checkbox,
  ListItemText,
  InputLabel,
  Tooltip,
  styled,
  Select,
} from "@mui/material";
import { useController } from "react-hook-form";

const CustomBorderSelect = styled(Select)`
  &.Mui-focused {
    & fieldset {
      border-color: #3a3a3a !important;
    }
  }
`;

const SelectField = ({
  name,
  control,
  label,
  data,
  labelKey = "label",
  valueKey = "value",
  error,
  multiSelect = false,
  showSelectAll = false,
  onChange,
  placeholder,
  ...props
}) => {
  const { field } = useController({
    name,
    control,
    defaultValue: multiSelect ? [] : "",
  });

  const [isAllSelected, setIsAllSelected] = useState(false);

  useEffect(() => {
    if (multiSelect && data.length > 0) {
      const allValues = data.map((item) => item[valueKey]);
      setIsAllSelected(
        field.value.length === allValues.length &&
          allValues.every((val) => field.value.includes(val))
      );
    }
  }, [field.value, data, multiSelect, valueKey]);

  const handleSelectAll = () => {
    if (multiSelect) {
      if (isAllSelected) {
        field.onChange([]);
        setIsAllSelected(false);
      } else {
        const allValues = data.map((item) => item[valueKey]);
        field.onChange(allValues);
        setIsAllSelected(true);
      }
    }
  };

  const handleChange = (event) => {
    let value = event.target.value;
    if (multiSelect && value.includes("SELECT_ALL")) {
      handleSelectAll();
    } else {
      field.onChange(value);
    }
    onChange?.(value);
  };

  const renderValue = (selected) => {
    if (!selected || selected.length === 0) return placeholder;
    if (multiSelect) {
      const selectedOptions = data
        .filter((item) => selected.includes(item[valueKey]))
        .map((item) => item[labelKey])
        .join(", ");
      return <Tooltip title={selectedOptions}>{selectedOptions}</Tooltip>;
    }
    const selectedOption = data.find((item) => item[valueKey] === selected);
    return selectedOption ? selectedOption[labelKey] : placeholder;
  };

  return (
    <FormControl
      fullWidth
      error={Boolean(error)}
      size="small"
      variant="outlined"
    >
      {label && <InputLabel>{label}</InputLabel>}
      <CustomBorderSelect
        multiple={multiSelect}
        value={field.value}
        onChange={handleChange}
        displayEmpty
        renderValue={renderValue}
        {...props}
        sx={{
          fontFamily: "Poppins-Regular",
          fontSize: "14px",
        }}
        label={label}
      >
        {multiSelect && showSelectAll && (
          <MenuItem value="SELECT_ALL" className="p-0 pl-2">
            <ListItemText primary="Select All" />
            <Checkbox checked={isAllSelected} />
          </MenuItem>
        )}
        {data.map((item, index) => (
          <MenuItem key={index} value={item[valueKey]} className="p-0 pl-2">
            <ListItemText primary={item[labelKey]} />
            {multiSelect && (
              <Checkbox checked={field.value.includes(item[valueKey])} />
            )}
          </MenuItem>
        ))}
      </CustomBorderSelect>
      {error && <FormHelperText>{error}</FormHelperText>}
    </FormControl>
  );
};

export default SelectField;
